<template>
    <v-dialog
        v-model="dialog"
        max-width="800px"
        persistent
        scrollable
    >
        <v-card>
            <v-toolbar flat class="bg-gradient" height="64">
                <v-btn 
                    x-large 
                    depressed 
                    color="transparent" 
                    class="ml-2" 
                    dark 
                    @click="closeDialog"
                >
                    <v-icon small class="mr-2">$prev</v-icon>
                    Retour
                </v-btn>
                <v-spacer></v-spacer>
                <v-btn depressed :disabled="!isValid" color="lightgrey" outlined @click="saveButton">{{ $t('save') }}</v-btn>
            </v-toolbar>

            <v-card-text>
                <!-- Section Général -->
                <v-text-field
                    v-model="localButton.label"
                    label="Texte du bouton *"
                    :rules="[rules.required, rules.maxLength]"
                    counter="25"
                    hint="Maximum 25 caractères"
                    persistent-hint
                    flat
                    solo
                    dense
                    class="my-4 input-form"
                ></v-text-field>

                <v-select
                    v-model="localButton.action"
                    :items="buttonTypes"
                    item-text="text"
                    item-value="action"
                    label="Type d'action *"
                    :item-disabled="item => item.disabled === true"
                    class="mb-4 input-form"
                    flat
                    solo
                    dense
                    @change="onActionChange"
                ></v-select>

                <!-- Section Configuration -->
                <!-- Configuration pour Openurl -->
                <template v-if="localButton.action === 'Openurl'">
                    <v-text-field
                        v-model="localButton.url"
                        label="URL *"
                        :rules="[rules.url]"
                        placeholder="https://example.com"
                        class="mb-4 input-form"
                        flat
                        solo
                        dense
                    ></v-text-field>
                    
                    <v-expansion-panels v-model="webviewPanel" class="mb-4">
                        <v-expansion-panel>
                            <v-expansion-panel-header>
                                <span>Options Webview (optionnel)</span>
                            </v-expansion-panel-header>
                            <v-expansion-panel-content>
                                <v-select
                                    v-model="localButton.openUrlProperties.webviewMode"
                                    :items="webviewModes"
                                    item-text="text"
                                    item-value="value"
                                    label="Mode d'affichage"
                                    class="input-form"
                                    flat
                                    solo
                                    dense
                                ></v-select>
                            </v-expansion-panel-content>
                        </v-expansion-panel>
                    </v-expansion-panels>
                </template>

                <!-- Configuration pour Dial -->
                <template v-if="localButton.action === 'Dial'">
                    <v-text-field
                        v-model="localButton.dial.PhoneNumber"
                        label="Numéro de téléphone *"
                        :rules="[rules.phone]"
                        placeholder="+33612345678"
                        hint="Format international recommandé"
                        persistent-hint
                        class="input-form"
                        flat
                        solo
                        dense
                    ></v-text-field>
                </template>

                <!-- Configuration pour viewLocation -->
                <template v-if="localButton.action === 'viewLocation'">
                    <v-text-field
                        v-model="localButton.viewLocation.latitude"
                        label="Latitude *"
                        :rules="[rules.latitude]"
                        placeholder="48.8566"
                        class="mb-4 input-form"
                        flat
                        solo
                        dense
                    ></v-text-field>
                    <v-text-field
                        v-model="localButton.viewLocation.longitude"
                        label="Longitude *"
                        :rules="[rules.longitude]"
                        placeholder="2.3522"
                        class="mb-4 input-form"
                        flat
                        solo
                        dense
                    ></v-text-field>
                    <v-alert color="info" dense text>
                        Le label sera automatiquement synchronisé avec le texte du bouton.
                    </v-alert>
                </template>

                <!-- Configuration pour CreateCalendarEvent -->
                <template v-if="localButton.action === 'CreateCalendarEvent'">
                    <v-text-field
                        v-model="localButton.calendar.title"
                        label="Titre de l'événement *"
                        :rules="[rules.calendarTitle]"
                        counter="100"
                        class="mb-4 input-form"
                        flat
                        solo
                        dense
                    ></v-text-field>
                    <v-textarea
                        v-model="localButton.calendar.description"
                        label="Description"
                        :rules="[rules.calendarDescription]"
                        counter="500"
                        rows="3"
                        class="mb-4 input-form"
                        flat
                        solo
                        dense
                    ></v-textarea>
                    <v-row>
                        <v-col cols="12" md="6">
                            <VDateTimePicker
                                :model="localButton.calendar"
                                fieldName="startTime"
                                :field="{
                                    title: 'Date de début',
                                    props: {
                                        required: true,
                                        datePickerProps: localButton.calendar.endTime ? {
                                            maxDate: localButton.calendar.endTime.split(' ')[0]
                                        } : {}
                                    },
                                    rules: [{'fonction': 'required', 'params': []}]
                                }"
                                :hideDetails="false"
                            />
                        </v-col>
                        <v-col cols="12" md="6">
                            <VDateTimePicker
                                :model="localButton.calendar"
                                fieldName="endTime"
                                :field="{
                                    title: 'Date de fin',
                                    props: {
                                        required: true,
                                        datePickerProps: localButton.calendar.startTime ? {
                                            minDate: localButton.calendar.startTime.split(' ')[0]
                                        } : {}
                                    },
                                    rules: [{'fonction': 'required', 'params': []}]
                                }"
                                :hideDetails="false"
                            />
                        </v-col>
                    </v-row>
                </template>
            </v-card-text>

        </v-card>
    </v-dialog>
</template>

<script>
import validate from "../functions/validations";
import VDateTimePicker from "@/components/forms/inputs/v-datetime-picker";

export default {
    name: "VRcsButtonConfigDialog",
    components: { VDateTimePicker },
    mixins: [validate],
    props: {
        value: {
            type: Boolean,
            default: false
        },
        button: {
            type: Object,
            default: () => ({})
        },
        buttonIndex: {
            type: Number,
            default: null
        },
        totalButtons: {
            type: Number,
            default: 0
        },
        buttonTypes: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            dialog: false,
            webviewPanel: [],
            localButton: {},
            buttonTypeInfo: '',
            webviewModes: [
                { text: 'Plein écran', value: 'full' },
                { text: 'Demi-écran', value: 'half' },
                { text: 'Grand', value: 'tall' }
            ],
            rules: {
                required: v => !!v || 'Ce champ est requis',
                maxLength: v => !v || v.length <= 25 || 'Maximum 25 caractères',
                url: v => {
                    if (!v) return 'Ce champ est requis';
                    if (!this.isValidUrl(v)) return 'Le format de l\'URL n\'est pas correct.';
                    return true;
                },
                phone: v => {
                    if (!v) return 'Ce champ est requis';
                    if (!this.isValidPhone(v)) return 'Numéro de téléphone invalide';
                    return true;
                },
                latitude: v => {
                    if (!v) return 'La latitude est requise';
                    if (!this.isValidLatitude(v)) return 'Latitude invalide (doit être entre -90 et 90)';
                    return true;
                },
                longitude: v => {
                    if (!v) return 'La longitude est requise';
                    if (!this.isValidLongitude(v)) return 'Longitude invalide (doit être entre -180 et 180)';
                    return true;
                },
                calendarTitle: v => {
                    if (!v) return 'Le titre est requis';
                    if (v.length > 100) return 'Le titre ne peut pas dépasser 100 caractères';
                    return true;
                },
                calendarDescription: v => {
                    if (v && v.length > 500) return 'La description ne peut pas dépasser 500 caractères';
                    return true;
                }
            }
        };
    },
    watch: {
        value(newVal) {
            this.dialog = newVal;
            if (newVal) {
                this.initializeButton();
            }
        },
        dialog(newVal) {
            if (!newVal) {
                this.$emit('input', false);
            }
        },
        'localButton.action'() {
            // Quand l'action change, nettoyer puis réinitialiser
            this.onActionChange();
        },
        'localButton.label'() {
            if (this.localButton.action === 'viewLocation' && this.localButton.label) {
                if (!this.localButton.viewLocation) {
                    this.localButton.viewLocation = {
                        latitude: '',
                        longitude: '',
                        label: ''
                    };
                }
                this.localButton.viewLocation.label = this.localButton.label;
            }
        }
    },
    computed: {
        isValid() {
            if (!this.localButton.label) return false;
            
            switch (this.localButton.action) {
                case 'Openurl':
                    return !!this.localButton.url && this.isValidUrl(this.localButton.url);
                case 'Dial':
                    if (!this.localButton.dial || !this.localButton.dial.PhoneNumber) return false;
                    return this.isValidPhone(this.localButton.dial.PhoneNumber);
                case 'viewLocation':
                    return !!(this.localButton.viewLocation && 
                             this.localButton.viewLocation.latitude && 
                             this.localButton.viewLocation.longitude &&
                             this.isValidLatitude(this.localButton.viewLocation.latitude) &&
                             this.isValidLongitude(this.localButton.viewLocation.longitude));
                case 'CreateCalendarEvent':
                    if (!this.localButton.calendar) return false;
                    if (!this.localButton.calendar.title) return false;
                    if (!this.localButton.calendar.startTime) return false;
                    if (!this.localButton.calendar.endTime) return false;
                    const startDate = new Date(this.localButton.calendar.startTime);
                    const endDate = new Date(this.localButton.calendar.endTime);
                    return endDate > startDate;
                default:
                    return true;
            }
        }
    },
    methods: {
        initializeButton() {
            this.localButton = JSON.parse(JSON.stringify(this.button));
            // D'abord nettoyer, puis initialiser
            this.cleanButtonStructure();
            this.ensureButtonStructure();
        },
        ensureButtonStructure() {
            if (!this.localButton.action) this.localButton.action = 'Openurl';
            if (!this.localButton.label) this.localButton.label = '';
            
            const action = this.localButton.action;
            
            // Initialiser uniquement les propriétés nécessaires selon l'action
            if (action === 'Openurl') {
                if (this.localButton.url === undefined) {
                    this.$set(this.localButton, 'url', '');
                }
                if (!this.localButton.openUrlProperties) {
                    this.$set(this.localButton, 'openUrlProperties', {
                        application: 'WEBVIEW',
                        webviewMode: 'full'
                    });
                }
            } else if (action === 'Dial') {
                if (!this.localButton.dial) {
                    this.$set(this.localButton, 'dial', {
                        PhoneNumber: ''
                    });
                } else if (this.localButton.dial.PhoneNumber === undefined) {
                    this.$set(this.localButton.dial, 'PhoneNumber', '');
                }
            } else if (action === 'viewLocation') {
                if (!this.localButton.viewLocation) {
                    this.$set(this.localButton, 'viewLocation', {
                        latitude: '',
                        longitude: '',
                        label: this.localButton.label || ''
                    });
                } else {
                    if (this.localButton.viewLocation.latitude === undefined) {
                        this.$set(this.localButton.viewLocation, 'latitude', '');
                    }
                    if (this.localButton.viewLocation.longitude === undefined) {
                        this.$set(this.localButton.viewLocation, 'longitude', '');
                    }
                    if (this.localButton.label) {
                        this.$set(this.localButton.viewLocation, 'label', this.localButton.label);
                    }
                }
            } else if (action === 'CreateCalendarEvent') {
                if (!this.localButton.calendar) {
                    this.$set(this.localButton, 'calendar', {
                        title: '',
                        description: '',
                        startTime: '',
                        endTime: ''
                    });
                } else {
                    if (this.localButton.calendar.title === undefined) {
                        this.$set(this.localButton.calendar, 'title', '');
                    }
                    if (this.localButton.calendar.description === undefined) {
                        this.$set(this.localButton.calendar, 'description', '');
                    }
                    if (this.localButton.calendar.startTime === undefined) {
                        this.$set(this.localButton.calendar, 'startTime', '');
                    }
                    if (this.localButton.calendar.endTime === undefined) {
                        this.$set(this.localButton.calendar, 'endTime', '');
                    }
                }
            }
        },
       
        onActionChange() {
            // D'abord nettoyer les propriétés inutiles
            this.cleanButtonStructure();
            // Ensuite initialiser les nouvelles propriétés pour la nouvelle action
            this.ensureButtonStructure();
            // Forcer la mise à jour de la validation
            this.$forceUpdate();
        },
        cleanButtonStructure() {
            // Supprimer les propriétés inutiles selon l'action
            const action = this.localButton.action;
            
            // Supprimer les propriétés des autres actions en utilisant $delete pour la réactivité
            if (action !== 'Openurl') {
                if (this.localButton.hasOwnProperty('url')) {
                    this.$delete(this.localButton, 'url');
                }
                if (this.localButton.hasOwnProperty('openUrlProperties')) {
                    this.$delete(this.localButton, 'openUrlProperties');
                }
            }
            
            if (action !== 'Dial') {
                if (this.localButton.hasOwnProperty('dial')) {
                    this.$delete(this.localButton, 'dial');
                }
            }
            
            if (action !== 'viewLocation') {
                if (this.localButton.hasOwnProperty('viewLocation')) {
                    this.$delete(this.localButton, 'viewLocation');
                }
            }
            
            if (action !== 'CreateCalendarEvent') {
                if (this.localButton.hasOwnProperty('calendar')) {
                    this.$delete(this.localButton, 'calendar');
                }
            }
        },
        saveButton() {
            if (this.isValid) {
                // Nettoyer la structure avant de sauvegarder
                this.cleanButtonStructure();
                this.$emit('save', JSON.parse(JSON.stringify(this.localButton)));
                this.closeDialog();
            }
        },
        closeDialog() {
            this.dialog = false;
            this.$emit('input', false);
        },
        previousButton() {
            this.saveButton();
            this.$emit('previous');
        },
        nextButton() {
            this.saveButton();
            this.$emit('next');
        },
        isValidUrl(string) {
            try {
                new URL(string);
                return true;
            } catch (_) {
                return false;
            }
        },
        isValidPhone(string) {
            // Vérifier si il y a des espaces ou des caractères spéciaux
            if (string.includes(' ') || string.includes('(') || string.includes(')') || string.includes('-') || string.includes('/')) {
                return false;
            }
            // Vérifier si le numéro est valide
            const phoneRegex = /^[\+]?[0-9]{10,}$/;
            return phoneRegex.test(string);
        },
        isValidLatitude(string) {
            const lat = parseFloat(string);
            return !isNaN(lat) && lat >= -90 && lat <= 90;
        },
        isValidLongitude(string) {
            const lng = parseFloat(string);
            return !isNaN(lng) && lng >= -180 && lng <= 180;
        }
    }
};
</script>

<style lang="scss" scoped>
.enregistrer-btn {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    
    &:hover:not(.v-btn--disabled) {
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
        transform: translateY(-1px);
    }
    
    &.v-btn--disabled {
        opacity: 0.6;
    }
}

.input-form {
    .v-input__slot {
        background-color: var(--v-lightgrey-base) !important;
    }
}
</style>

